<script setup lang="ts">
  import { ref, computed } from 'vue'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { useTranslation } from '@/composables/useTranslation'
  import { useToast } from '@/components/ui/toast'
  import AutoSavingForm from '@/components/ui/form-ui/AutoSavingForm.vue'
  import { CasesService } from '@/types/generated'
  import { useRouter } from 'vue-router'
  import { ChevronRightIcon } from 'lucide-vue-next'

  const { t } = useTranslation({
    en: {
      title: 'Note',
      viewAllNotes: 'View all notes',
      placeholder: 'Add notes about this case...',
      saveError: 'Failed to update notes',
      template: `Case Summary
-------------------
Client's Objective:
Key Issues:
Important Dates:

Legal Analysis
-------------------
Applicable Laws:
Precedents:
Potential Arguments:

Action Items
-------------------
[ ]
[ ]

Meeting Notes
-------------------
Date:
Attendees:
Key Points:

Next Steps
-------------------
1.
2.

Additional Notes
-------------------`
    },
    de: {
      title: 'Notiz',
      viewAllNotes: 'Alle Notizen ansehen',
      placeholder: 'Fügen Sie Notizen zu diesem Fall hinzu...',
      saveError: 'Fehler beim Aktualisieren der Notizen',
      template: `Fallzusammenfassung
-------------------
Ziel des Mandanten:
Hauptthemen:
Wichtige Termine:

Rechtliche Analyse
-------------------
Anwendbare Gesetze:
Präzedenzfälle:
Mögliche Argumente:

Aufgaben
-------------------
[ ]
[ ]

Besprechungsnotizen
-------------------
Datum:
Teilnehmer:
Kernpunkte:

Nächste Schritte
-------------------
1.
2.

Zusätzliche Notizen
-------------------`
    }
  })

  const caseStore = useCaseStore()
  const { toast } = useToast()

  const router = useRouter()
  const formValues = ref({
    notes: ''
  })

  const formMeta = ref({
    valid: true,
    errors: {}
  })

  // Set initial notes or template
  const initializeNotes = () => {
    const notes = caseStore.caseRecord?.note
    if (!notes || notes.trim() === '') {
      formValues.value.notes = t('template')
    } else {
      formValues.value.notes = notes
    }
  }
  initializeNotes()

  const handleSave = async (values: { notes: string }) => {
    try {
      await CasesService.casesPartialUpdate({
        uuid: caseStore.caseRecord?.uuid || '',
        requestBody: {
          note: values.notes
        }
      })
      // Update the store with the new notes
      if (caseStore.caseRecord) {
        caseStore.caseRecord.note = values.notes
      }
    } catch (error) {
      console.error(error)
      toast({
        title: t('saveError'),
        variant: 'destructive'
      })
    }
  }

  // Navigate to all notes view
  const navigateToAllNotes = () => {
    if (caseStore.caseRecord?.uuid) {
      router.push(`/app/cases/${caseStore.caseRecord.uuid}/notes`)
    }
  }
</script>

<template>
  <div>
    <div class="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-100">
      <!-- Note Content Area -->
      <div class="px-4 pb-2">
        <AutoSavingForm
          :values="formValues"
          :meta="formMeta"
          @save="handleSave"
        >
          <textarea
            v-model="formValues.notes"
            :placeholder="t('placeholder')"
            class="min-h-[300px] w-full border-0 font-mono text-sm focus:outline-none focus:ring-0"
            style="white-space: pre-wrap; resize: none"
          />
        </AutoSavingForm>
      </div>
    </div>
    <!-- View All Notes Link -->
    <div class="items-left mt-4 flex justify-end">
      <a
        href="#"
        class="inline-flex items-center text-sm font-medium text-purple-600 underline hover:text-purple-800"
        @click.prevent="navigateToAllNotes"
      >
        {{ t('viewAllNotes') }}
        <ChevronRightIcon class="ml-1 h-4 w-4" />
      </a>
    </div>
  </div>
</template>

<style scoped>
  textarea {
    resize: none;
  }
</style>
